import { SupportedLanguages } from '@constants/constants.type';

export const COMPANY_SETTINGS_COUNTRY_ID = 'id';
export const COMPANY_SETTINGS_COUNTRY_NAME = 'name';
export const COMPANY_SETTINGS_COUNTRY_ISO_CODE = 'isoCode';

export interface CompanySettingsCountry {
  [COMPANY_SETTINGS_COUNTRY_ID]: string;
  [COMPANY_SETTINGS_COUNTRY_NAME]: string;
  [COMPANY_SETTINGS_COUNTRY_ISO_CODE]: string;
}

export const COMPANY_VAT_NUMBER_COUNTRY_CODE = 'countryCode';
export const COMPANY_VAT_NUMBER_NUMBER = 'number';

export const COMPANY_BANKING_DETAILS_BANK_NUMBER = 'bankNumber';
export const COMPANY_BANKING_DETAILS_BANK_CODE = 'bankCode';

export const COMPANY_BILLING_INFO_LEGAL_NAME = 'legalName';
export const COMPANY_BILLING_INFO_REGISTERED_ADDRESS = 'registeredAddress';
export const COMPANY_BILLING_INFO_POSTCODE = 'postcode';
export const COMPANY_BILLING_INFO_CITY = 'city';
export const COMPANY_SETTINGS_COUNTRY = 'country';
export const COMPANY_BILLING_INFO_COUNTRY = 'billingCountry';
export const COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON = 'isIndividualPerson';
export const COMPANY_BILLING_INFO_VAT_NUMBER = 'vatNumber';
export const COMPANY_BILLING_INFO_CODICE_FISCALE = 'codiceFiscale';
export interface CompanyBillingInfo {
  [COMPANY_BILLING_INFO_LEGAL_NAME]?: string;
  [COMPANY_BILLING_INFO_REGISTERED_ADDRESS]?: string;
  [COMPANY_BILLING_INFO_POSTCODE]?: string;
  [COMPANY_BILLING_INFO_CITY]?: string;
  [COMPANY_BILLING_INFO_CODICE_FISCALE]?: string;
  [COMPANY_SETTINGS_COUNTRY]?: CompanySettingsCountry;
  [COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]?: boolean;
  [COMPANY_BILLING_INFO_VAT_NUMBER]?: string;
}

export const COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME = 'accountHolderName';
export const COMPANY_BANKING_DETAILS_ACCOUNT_NUMBER = 'accountNumber';
export const COMPANY_BANKING_DETAILS_COUNTRY = 'bankingCountry';

export interface CompanyBankingDetails {
  [COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME]?: string;
  [COMPANY_BANKING_DETAILS_BANK_NUMBER]?: string;
  [COMPANY_BANKING_DETAILS_BANK_CODE]?: string;
  [COMPANY_SETTINGS_COUNTRY]?: CompanySettingsCountry;
}
export const COMPANY_FINANCE_NOTIFICATIONS_PHONE = 'phone';
export const COMPANY_FINANCE_NOTIFICATIONS_EMAILS = 'emails';

export interface CompanyFinanceNotifications {
  [COMPANY_FINANCE_NOTIFICATIONS_PHONE]?: string;
  [COMPANY_FINANCE_NOTIFICATIONS_EMAILS]?: string[];
}

export const COMPANY_SETTING_ID = 'id';
export const COMPANY_SETTING_PUBLIC_NAME = 'publicName';
export const COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS = 'customerSupportEmails';
export const COMPANY_SETTING_BILLING_INFO = 'billingInfo';
export const COMPANY_SETTING_BANKING_DETAILS = 'bankingDetails';
export const COMPANY_SETTING_FINANCE_NOTIFICATIONS = 'financeNotifications';
export const COMPANY_SETTING_LOGO_URL = 'logoUrl';

export interface CompanySettings {
  [COMPANY_SETTING_ID]: string;
  [COMPANY_SETTING_LOGO_URL]?: string;
  [COMPANY_SETTING_PUBLIC_NAME]?: string;
  [COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]?: string[];
  [COMPANY_SETTING_BILLING_INFO]: CompanyBillingInfo;
  [COMPANY_SETTING_BANKING_DETAILS]: CompanyBankingDetails;
  [COMPANY_SETTING_FINANCE_NOTIFICATIONS]: CompanyFinanceNotifications;
}

/**
 * Organizer public profile — the fields the Public info block gains behind the
 * `organizer-public-profile` flag. Declared here in one go, ahead of the tickets that render each
 * control, so four parallel branches do not collide in this file and in the form config.
 *
 * These are the FRONT-END form keys. The API field names are agreed with B2B-2223 and mapped in
 * the integration ticket; do not assume the two match.
 */
export const COMPANY_SETTING_DESCRIPTION = 'description';
export const COMPANY_SETTING_DESCRIPTION_TRANSLATIONS = 'descriptionTranslations';
export const COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED = 'areTranslationsEnabled';
/** Not a form key: drives the translations block's base language, it is never edited directly. */
export const COMPANY_SETTING_DEFAULT_LOCALE = 'defaultLocale';
export const COMPANY_SETTING_CITIES = 'cities';
export const COMPANY_SETTING_MUSIC_GENRES = 'musicGenres';
export const COMPANY_SETTING_VIBES = 'vibes';
export const COMPANY_SETTING_IS_PUBLIC_PROFILE = 'isPublicProfile';
/** Not a form key: server-derived and read-only, and not rendered anywhere in the mock. */
export const COMPANY_SETTING_SLUG = 'slug';

/** Mirrors of the server-side guards. The API enforces all of them regardless of the form. */
export const COMPANY_PUBLIC_PROFILE_MAX_MUSIC_GENRES = 3;
export const COMPANY_PUBLIC_PROFILE_MAX_VIBES = 3;
export const COMPANY_PUBLIC_PROFILE_MAX_DESCRIPTION_LENGTH = 1000;

/**
 * Per-locale description. Only the locales that carry a translation are present — the base
 * locale's copy lives in `COMPANY_SETTING_DESCRIPTION`, not in here.
 */
export type CompanyDescriptionTranslations = Partial<Record<SupportedLanguages, string>>;

export const COMPANY_SETTINGS_IMAGE_INPUT_NAME = 'logoUrl';

export interface CompanySettingsFormTypes {
  [COMPANY_SETTINGS_IMAGE_INPUT_NAME]: string;
  [COMPANY_SETTING_PUBLIC_NAME]: string;
  [COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]: string;
  /**
   * Organizer public profile. Optional on the form type because the whole block is behind
   * `organizer-public-profile`: with the flag off none of these keys are registered, which is what
   * keeps the existing Save button's dirty state untouched.
   */
  [COMPANY_SETTING_DESCRIPTION]?: string;
  [COMPANY_SETTING_DESCRIPTION_TRANSLATIONS]?: CompanyDescriptionTranslations;
  [COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED]?: boolean;
  [COMPANY_SETTING_CITIES]?: string[];
  [COMPANY_SETTING_MUSIC_GENRES]?: string[];
  [COMPANY_SETTING_VIBES]?: string[];
  [COMPANY_SETTING_IS_PUBLIC_PROFILE]?: boolean;
  [COMPANY_BILLING_INFO_COUNTRY]?: string;
  [COMPANY_BANKING_DETAILS_COUNTRY]?: string;
  [COMPANY_SETTING_BILLING_INFO]: {
    [COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]: string;
    [COMPANY_BILLING_INFO_LEGAL_NAME]: string;
    [COMPANY_BILLING_INFO_REGISTERED_ADDRESS]: string;
    [COMPANY_BILLING_INFO_POSTCODE]: string;
    [COMPANY_BILLING_INFO_CITY]: string;
    [COMPANY_BILLING_INFO_VAT_NUMBER]: string;
    [COMPANY_BILLING_INFO_CODICE_FISCALE]: string;
  };
  [COMPANY_SETTING_BANKING_DETAILS]: {
    [COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME]?: string;
    [COMPANY_BANKING_DETAILS_BANK_NUMBER]?: string;
    [COMPANY_BANKING_DETAILS_BANK_CODE]?: string;
  };
  [COMPANY_SETTING_FINANCE_NOTIFICATIONS]: {
    [COMPANY_FINANCE_NOTIFICATIONS_PHONE]: string;
    [COMPANY_FINANCE_NOTIFICATIONS_EMAILS]: string;
  };
}
